import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/Reveal";
import MaskedLines from "@/components/MaskedLines";
import { ABOUT } from "@/constants/testIds";
import { ASSETS, MEDIA } from "@/lib/config";
import useAutoPlayVideo from "@/hooks/useAutoPlayVideo";

const chapters = [
  { n: "I", title: "Slow down.",                  copy: "Time is the only currency that never returns. Spend it deliberately." },
  { n: "II", title: "Protect your peace.",         copy: "Guard the quiet. It is where the truest work is done." },
  { n: "III", title: "Stay present.",              copy: "The moment is the only place you have ever actually lived." },
  { n: "IV", title: "Strengthen your body.",       copy: "The body is the vehicle. Honor it with the effort it deserves." },
  { n: "V", title: "Expand your mind.",            copy: "Read what challenges you. Think what unsettles you." },
  { n: "VI", title: "Grow spiritually.",           copy: "Anchor yourself in something greater than the day's weather." },
  { n: "VII", title: "Build meaningful bonds.",    copy: "You become who you walk with. Choose the company well." },
  { n: "VIII", title: "Pursue purpose.",           copy: "Do the work that will still matter in ten years. Leave the rest." },
];

export default function About() {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [videoInView, setVideoInView] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Lazy-mount video only when the section is close to the viewport
  // (saves ~1-2MB of upfront bandwidth for users who never scroll here)
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setVideoInView(true);
      return;
    }
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVideoInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px" } // pre-mount ~1 viewport before entry
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Robust autoplay: retries on ready, visibility, focus, first user gesture, etc.
  useAutoPlayVideo(videoRef, { enabled: !reducedMotion && videoInView });

  // Parallax on the background video
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const videoY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.2]);

  return (
    <section
      ref={sectionRef}
      id="about"
      data-testid={ABOUT.root}
      className="relative text-[#e8ecef] overflow-hidden"
      style={{
        backgroundImage:
          "linear-gradient(to bottom, #0a0a0a 0%, #0f1216 20%, #0f1216 80%, #14242e 100%)",
      }}
    >
      {/* -------- Manifesto — video background + masked reveals -------- */}
      <div className="relative min-h-[100svh]">
        {/* Video background (behind text) */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Always-mounted poster — instant visual */}
          <img
            src={MEDIA.aboutPoster}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
            loading="lazy"
          />
          {!reducedMotion && videoInView && (
            <motion.video
              ref={videoRef}
              poster={MEDIA.aboutPoster}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              controls={false}
              disablePictureInPicture
              aria-hidden="true"
              data-testid="about-video"
              style={{ y: videoY, scale: videoScale, pointerEvents: "none" }}
              className="absolute inset-0 w-full h-full object-cover object-center will-change-transform"
            >
              <source src={MEDIA.aboutVideoWebm} type="video/webm" />
              <source src={MEDIA.aboutVideoMp4} type="video/mp4" />
            </motion.video>
          )}

          {/* Dark overlays for legibility */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_50%,rgba(10,10,10,0.15)_0%,rgba(10,10,10,0.55)_60%,rgba(10,10,10,0.85)_100%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-transparent to-[#0f1216]" />
          {/* Left-side text column scrim */}
          <div className="absolute inset-y-0 left-0 w-full lg:w-[45%] bg-gradient-to-r from-[#0a0a0a]/80 via-[#0a0a0a]/30 to-transparent pointer-events-none" />

          {/* Grain */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-30 mix-blend-overlay pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>\")",
            }}
          />
        </div>

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 py-32 md:py-48">
          {/* Header */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
            <div className="lg:col-span-6 lg:sticky lg:top-40">
              <Reveal>
                <div className="flex items-center gap-4 mb-10">
                  <span className="block h-px w-14 md:w-24 bg-[#c9d3d8]/60" />
                  <p className="text-[11px] tracking-[0.5em] uppercase text-[#c9d3d8]">
                    Chapter 01 — The Ethos
                  </p>
                </div>
              </Reveal>

              <MaskedLines
                as="h2"
                whileInView
                lines={["More than", "apparel."]}
                delay={0.05}
                stagger={0.1}
                duration={1.1}
                className="font-serif text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.98] tracking-tight font-light"
                lineClassName="text-[#e8ecef]"
              />

              <Reveal delay={0.35}>
                <p className="mt-12 text-[#c9d3d8] text-base md:text-lg leading-relaxed max-w-md font-light">
                  VISION is a quiet reminder — a movement for people committed
                  to growth, presence, and becoming the fullest version of
                  themselves.
                </p>
              </Reveal>

              <Reveal delay={0.5}>
                <div className="mt-14 flex items-center gap-6 text-[#c9d3d8]/80">
                  <span className="block h-px w-10 bg-[#c9d3d8]/40" />
                  <span className="text-[10px] tracking-[0.5em] uppercase">
                    A Manifesto in VIII
                  </span>
                </div>
              </Reveal>
            </div>

            {/* Numbered manifesto chapters */}
            <div className="lg:col-span-6">
              <ul className="divide-y divide-[#c9d3d8]/10">
                {chapters.map((c, i) => (
                  <Reveal
                    key={c.n}
                    as="li"
                    delay={i * 0.04}
                    className="group py-7 md:py-8 flex items-start gap-6 md:gap-10"
                  >
                    <span className="text-[#c9d3d8]/60 text-[11px] tracking-[0.32em] font-mono pt-3 w-12 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-serif text-2xl md:text-4xl leading-[1.05] tracking-tight text-[#e8ecef] transition-colors duration-500 group-hover:text-[#c9d3d8]">
                        {c.title}
                      </h3>
                      <p className="mt-3 text-sm md:text-[15px] text-[#c9d3d8]/70 leading-relaxed max-w-[42ch] font-light">
                        {c.copy}
                      </p>
                    </div>
                    <span
                      aria-hidden
                      className="hidden md:block text-[#c9d3d8]/40 text-xs tracking-[0.3em] pt-3"
                    >
                      {c.n}
                    </span>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Full-bleed magazine spread — pagoda + green panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
        <div className="relative aspect-square lg:aspect-auto overflow-hidden">
          <img
            src={ASSETS.pagoda}
            alt="A quiet act of design — the pagoda"
            className="w-full h-full object-cover hidden lg:block fade-right"
            draggable={false}
            loading="lazy"
          />
          <img
            src={ASSETS.pagoda}
            alt=""
            aria-hidden
            className="w-full h-full object-cover lg:hidden"
            draggable={false}
            loading="lazy"
          />
        </div>
        <div
          className="relative flex flex-col justify-center px-8 md:px-14 lg:px-20 py-16 md:py-24 lg:py-0 min-h-[560px] lg:min-h-0 grain"
          style={{ backgroundColor: "#1f3d31" }}
        >
          <p className="font-serif text-[#e8ecef]/35 text-6xl md:text-7xl leading-none mb-8">
            0.
          </p>
          <p className="text-[#e8ecef]/80 text-[10px] md:text-xs tracking-[0.42em] uppercase mb-8">
            Presence · Kyoto
          </p>
          <MaskedLines
            as="p"
            whileInView
            lines={["A quiet act,", "repeated,", "becomes a temple."]}
            stagger={0.09}
            duration={1.05}
            className="font-serif text-[#e8ecef] text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight font-light max-w-[16ch]"
          />
          <p className="mt-10 text-[#e8ecef]/75 text-sm md:text-base leading-relaxed font-light max-w-md">
            Before it is a brand, VISION is a discipline — a practice of paying
            attention. This is where the journey begins.
          </p>
        </div>
      </div>
    </section>
  );
}
